import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { addMonths, format, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Cabeçalho compartilhado dos calendários.
 * Mantém mês e ano como alvos independentes para evitar a alternância ambígua
 * do cabeçalho padrão do MUI.
 */
const DatePickerCalendarHeader = ({
  currentMonth,
  onMonthChange,
  onViewChange,
  disabled = false,
  minDate,
  maxDate
}) => {
  const previousMonth = addMonths(currentMonth, -1);
  const nextMonth = addMonths(currentMonth, 1);
  const previousDisabled = disabled || (minDate && startOfMonth(previousMonth) < startOfMonth(minDate));
  const nextDisabled = disabled || (maxDate && startOfMonth(nextMonth) > startOfMonth(maxDate));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 1, pb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 0.5 }}>
        <Typography
          component="button"
          type="button"
          onClick={() => onViewChange?.('month')}
          sx={{
            appearance: 'none',
            border: 0,
            background: 'transparent',
            color: 'text.primary',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 500,
            p: 0.5,
            textTransform: 'capitalize',
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          {format(currentMonth, 'MMMM', { locale: ptBR })}
        </Typography>
        <Typography
          component="button"
          type="button"
          onClick={() => onViewChange?.('year')}
          sx={{
            appearance: 'none',
            border: 0,
            background: 'transparent',
            color: 'text.primary',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 500,
            p: 0.5,
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          {format(currentMonth, 'yyyy')}
        </Typography>
      </Box>

      <IconButton
        size="small"
        aria-label="Mês anterior"
        disabled={previousDisabled}
        onClick={() => onMonthChange(previousMonth, 'right')}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Próximo mês"
        disabled={nextDisabled}
        onClick={() => onMonthChange(nextMonth, 'left')}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </Box>
  );
};

DatePickerCalendarHeader.propTypes = {
  currentMonth: PropTypes.instanceOf(Date).isRequired,
  onMonthChange: PropTypes.func.isRequired,
  onViewChange: PropTypes.func,
  disabled: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date)
};

export default DatePickerCalendarHeader;
